import React, { useEffect, useState } from 'react';

import { Layout } from 'antd';
import { Outlet } from 'react-router-dom';

import AuthApi from '@apis/auth/auth.api';
import { useGetKeysQuery } from '@apis/google/Google.api';
import { useUpdateLastLoginMutation } from '@apis/users/Users.api';
import { Loader } from '@atoms/loader/Loader';
import { addGoogleScript } from '@configs/AddGoogleScript';
import { getUpdateLastLogin, setUpdateLastLogin } from '@helpers/handleUser';
import {
    LOCAL_STORAGE_FIELDS,
    setLocalStorageItem
} from '@helpers/localStorage';
import { useUserSettings } from '@hooks/useUserSettings';
import { Header } from '@organisms/header/Header';
import { Sidebar } from '@organisms/sidebar/Sidebar';
import { ErrorFallback } from '@pages/errorFallback/ErrorFallback.page';

import styles from './MainLayout.module.less';

export const MainLayout = () => {
    const [updateLastLogin] = useUpdateLastLoginMutation();
    const [isSiderOpenOnMobile, setSiderOpenOnMobile] =
        useState<boolean>(false);
    const { data: googleData } = useGetKeysQuery();

    const {
        loading: userSettingsLoading,
        isDarkModeEnabled,
        isUserError
    } = useUserSettings();

    useEffect(() => {
        if (!userSettingsLoading && isDarkModeEnabled !== undefined) {
            document.documentElement.setAttribute(
                'data-theme',
                isDarkModeEnabled ? 'dark' : 'light'
            );
            setLocalStorageItem(
                LOCAL_STORAGE_FIELDS.DARK_MODE,
                String(isDarkModeEnabled)
            );
        }
    }, [userSettingsLoading]);

    useEffect(() => {
        if (!getUpdateLastLogin()) {
            updateLastLogin();
            setUpdateLastLogin();
        }
    }, []);

    const actions = {
        signOut: AuthApi.signOut
    };

    const showError = isUserError;
    const showContent = !userSettingsLoading && !isUserError;

    const resetScreen = () => {
        window.location.reload();
    };

    useEffect(() => {
        if (googleData?.data) {
            addGoogleScript(googleData.data.googleAnalyticsKey);
        }
    }, [googleData]);

    return (
        <>
            {showError && (
                <div className={styles.Error}>
                    <ErrorFallback
                        title="Unexpected error occurred"
                        resetErrorBoundary={resetScreen}
                    />
                </div>
            )}
            {userSettingsLoading && (
                <div className={styles.LoaderContainer}>
                    <Loader />
                </div>
            )}
            {showContent && (
                <>
                    <Sidebar
                        isSiderOpen={isSiderOpenOnMobile}
                        toggleSiderOnMobile={setSiderOpenOnMobile}
                        signOut={actions.signOut}
                    />
                    <Layout>
                        <div className={styles.Content}>
                            <Header
                                actions={actions}
                                toggleSiderOnMobile={setSiderOpenOnMobile}
                                isSiderOpenOnMobile={isSiderOpenOnMobile}
                            />
                            <div className={styles.MainContent}>
                                <div className={styles.MainWrapper}>
                                    <Outlet />
                                </div>
                            </div>
                        </div>
                    </Layout>
                </>
            )}
        </>
    );
};
